// Release behaviour for DraggableSheet (DraggableScrollableSheet free / snapping modes)
// and the settle timing shared with BottomSheet.

import { durations } from '../theme/motion';

export interface SheetReleaseInput {
  /** Current height as a fraction of the viewport. */
  fraction: number;
  /** Release velocity in viewport fractions per second, positive = shrinking (finger moving down). */
  velocity: number;
  min: number;
  max: number;
  /** Snap sizes (fractions); empty for free resizing. */
  snaps: readonly number[];
  /** Velocity (fractions/s) above which a release counts as a fling. */
  flingThreshold: number;
}

export type SheetRelease = { kind: 'close' } | { kind: 'settle'; fraction: number };

/** Distance a free fling keeps travelling, as seconds of the release velocity (ballistic approximation). */
const FREE_FLING_SECONDS = 0.25;

export function resolveSheetRelease({ fraction, velocity, min, max, snaps, flingThreshold }: SheetReleaseInput): SheetRelease {
  if (snaps.length === 0) {
    const projected = Math.min(max, fraction - velocity * FREE_FLING_SECONDS);
    if (projected <= min + 1e-3) return { kind: 'close' };
    return { kind: 'settle', fraction: projected };
  }
  const stops = Array.from(new Set([min, ...snaps, max])).filter((s) => s >= min && s <= max).sort((a, b) => a - b);
  if (fraction < min - 1e-3) return { kind: 'close' };
  if (Math.abs(velocity) >= flingThreshold) {
    if (velocity > 0) {
      const lower = [...stops].reverse().find((s) => s < fraction - 1e-3);
      return lower === undefined ? { kind: 'close' } : { kind: 'settle', fraction: lower };
    }
    const higher = stops.find((s) => s > fraction + 1e-3);
    return { kind: 'settle', fraction: higher ?? max };
  }
  let nearest = stops[0] ?? min;
  for (const s of stops) if (Math.abs(s - fraction) < Math.abs(nearest - fraction)) nearest = s;
  return { kind: 'settle', fraction: nearest };
}

/** Initial slope of easeOutCubic (its first control point, 0.61 / 0.215). */
const EASE_OUT_CUBIC_START_SLOPE = 0.61 / 0.215;

/**
 * Duration (ms) for a sheet settling `distance` px after a release at
 * `velocity` px/s, eased with easeOutCubic so it leaves at the finger's speed
 * (polish pass; Flutter animates the remaining travel at a fixed rate, which
 * visibly brakes a fast fling). Velocity pointing away from the target is
 * ignored. Clamped to swipeMin..swipeDismiss (120..280 ms).
 */
export function settleDurationMs(
  distance: number,
  velocity: number,
  minMs: number = durations.swipeMin,
  maxMs: number = durations.swipeDismiss,
): number {
  const d = Math.abs(distance);
  if (d < 0.5) return minMs;
  const towards = Math.sign(velocity) === Math.sign(distance) ? Math.abs(velocity) : 0;
  if (towards < 1) return maxMs;
  return Math.min(maxMs, Math.max(minMs, Math.round((1000 * d * EASE_OUT_CUBIC_START_SLOPE) / towards)));
}
